import React, { useEffect, useMemo } from 'react';
import { isNullOrUndefined } from 'utils/Global';
import { RMRectangle } from 'business/models/CustomModels';
import {
  ContainerDrop,
  DisplayMode,
  ScoresOfWellDrops,
  WellIng,
} from 'components/Experiment/constants';
import { selectExperimentLayers } from 'redux/selectors/ExperimentStateSelector';
import { useRMSelector } from 'redux/hooks';
import WellDesignRenderer from './wellDesignRenderer';
import IngredientDetailRenderer from '../wellIngredientView/ingredientsDetailsRenderer';

interface Props {
  canvasRefs: any;
  grpRef: any;
  displayMode: DisplayMode;
  height: number;
  width: number;
  ingredientsWells: WellIng[];
  rows: number;
  cols: number;
  wellRects: RMRectangle[];
  wellRectsPadded: RMRectangle[];
  maxWellVolume?: number;
  containerDropList?: ContainerDrop[];
  dropNumber?: number;
  maxLengthOfPlate?: number;
  ingredients?: any;
  shouldCacheLayer?: boolean;
  layerCachePixelRatio?: number;
  plateNumber?: number;
  platePosition?: { colPos: number; rowPos: number };
  shouldShowIngredients?: boolean;
  strokeNormalizer: number;
  highlightedWellNumbers?: Array<number>;
  wellScores?: ScoresOfWellDrops[];
}

function PlateIngredientRenderer(props: Props) {
  const {
    canvasRefs,
    displayMode,
    height,
    width,
    ingredientsWells,
    rows,
    cols,
    wellRects: wellRectsProp,
    wellRectsPadded,
    maxWellVolume = 0,
    containerDropList,
    dropNumber,
    maxLengthOfPlate,
    ingredients = null,
    shouldCacheLayer = false,
    layerCachePixelRatio = 1,
    plateNumber = 1,
    platePosition,
    shouldShowIngredients = true,
    strokeNormalizer,
    highlightedWellNumbers,
    wellScores,
  } = props;

  //RFR: need to come up with a better approach that avoids using hooks.
  const layers = useRMSelector(selectExperimentLayers);
  const wells = useMemo(() => {
    const _wells = [];
    if (isNullOrUndefined(ingredientsWells) || isNullOrUndefined(wellRectsPadded)) {
      return _wells;
    }

    const wellPadding = wellRectsPadded[0].w - wellRectsProp[0].w;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const wellNum = row * cols + col;
        const wellWithIngredients = ingredientsWells[wellNum];
        const wellRectangle = wellRectsPadded[wellNum];

        if (
          !wellWithIngredients ||
          !wellRectangle ||
          (maxLengthOfPlate !== undefined && wellNum > maxLengthOfPlate)
        ) {
          continue;
        }
        const wellPositionDetails = {
          x: col + (platePosition.colPos - 1) * cols,
          y: row + (platePosition.rowPos - 1) * rows,
          colPos: platePosition.colPos,
          rowPos: platePosition.rowPos,
        };

        if (displayMode === DisplayMode.DESIGN) {
          _wells.push(
            <WellDesignRenderer
              wellData={wellWithIngredients}
              wellRectangle={wellRectangle}
              maxWellVolume={maxWellVolume || wellWithIngredients.wellVolume}
              dropsList={containerDropList || wellWithIngredients.dropList}
              plateNumber={plateNumber}
              wellPadding={wellPadding}
              wellPositionDetails={wellPositionDetails}
              shouldShowIngredients={shouldShowIngredients}
              strokeNormalizer={strokeNormalizer}
              shouldBlurWell={
                highlightedWellNumbers?.length > 0 &&
                !highlightedWellNumbers.includes(row * cols + col + 1)
              }
            />,
          );
        } else {
          const hasScores =
            wellScores?.filter(
              (ws) => ws.wellNumber === wellNum + 1 && ws.dropNumber === dropNumber,
            )?.length > 0;
          _wells.push(
            <IngredientDetailRenderer
              {...wellRectangle}
              wellData={wellWithIngredients}
              dropNumber={dropNumber}
              layers={layers}
              wellPositionDetails={wellPositionDetails}
              hasScores={hasScores}
            />,
          );
        }
      }
    }
    return _wells;
  }, [
    ingredientsWells,
    dropNumber,
    width,
    height,
    displayMode,
    ingredients,
    shouldShowIngredients,
    highlightedWellNumbers,
  ]);

  useEffect(() => {
    const { layerRef } = canvasRefs();
    if (shouldCacheLayer && layerRef != undefined && wells.length > 0) {
      layerRef.cache({ pixelRatio: layerCachePixelRatio, imageSmoothingEnabled: true });
    }
  }, [wells]);

  return <>{wells}</>;
}

export default PlateIngredientRenderer;
